import { useEffect, useMemo, useState } from "react";
import { Form, InputGroup, Button, Alert } from "react-bootstrap";
import UlpsPortalApiClient from "../UlpsPortalApiClient";
import Util from "../Util";

export default function FeatureGroupForm({ isEdit = false }) {
  const NAME_MIN = 3;
  const NAME_MAX = 100;
  const DESC_MAX = 512;
  const featureGroupId = Util.getQuerySearch("id");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [licenceSchema, setLicenceSchema] = useState("");
  const [licenceSchemas, setLicenceSchemas] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [existingGroups, setExistingGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      UlpsPortalApiClient.getFeatureGroups(),
      UlpsPortalApiClient.getLicenceSchemas(),
      isEdit ? UlpsPortalApiClient.getFeatureGroup(featureGroupId) : Promise.resolve(null),
    ])
      .then(([groups, schemas, featureGroup]) => {
        setExistingGroups(groups || []);
        setLicenceSchemas(schemas || []);

        if (featureGroup) {
          setName(featureGroup.name || "");
          setDescription(featureGroup.description || "");
          setLicenceSchema(featureGroup.licenceSchema || "");
          setSelectedFeatures(Array.isArray(featureGroup.features) ? featureGroup.features : []);
        }
      })
      .catch((e) => {
        console.error(e);
        setError(`Failed to load feature group ${isEdit ? "details" : "form data"}.`);
      })
      .finally(() => setLoading(false));
  }, [featureGroupId, isEdit]);

  useEffect(() => {
    if (!licenceSchema) {
      setAllFeatures([]);
      setSelectedFeatures([]);
      return;
    }

    UlpsPortalApiClient.getFeatures(licenceSchema)
      .then((features) => {
        const productFeatures = features || [];
        setAllFeatures(productFeatures);
        setSelectedFeatures((prev) => prev.filter((feature) => productFeatures.some((item) => item.id === feature.id)));
      })
      .catch((e) => {
        console.error("Failed to load product features.", e);
        setAllFeatures([]);
      });
  }, [licenceSchema]);

  const selectableFeatures = useMemo(
    () => allFeatures.filter(feature => !selectedFeatures.some(selected => selected.id === feature.id)),
    [allFeatures, selectedFeatures]
  );

  const validate = () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    if (trimmedName.length < NAME_MIN) {
      return `Feature group name must be at least ${NAME_MIN} characters.`;
    }

    if (trimmedName.length > NAME_MAX) {
      return `Feature group name must be at most ${NAME_MAX} characters.`;
    }

    if (!licenceSchema) {
      return "Feature group product is required.";
    }

    if (trimmedDescription.length > DESC_MAX) {
      return `Description must be at most ${DESC_MAX} characters.`;
    }

    const conflict = existingGroups.find(group =>
      group.licenceSchema === licenceSchema &&
      group.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
      (!isEdit || Number(group.id) !== Number(featureGroupId))
    );

    if (conflict) {
      return `Feature group name "${trimmedName}" already exists for product "${licenceSchema}".`;
    }

    return null;
  };

  const submit = async () => {
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);

    const payload = {
      name: name.trim(),
      description: description.trim(),
      features: selectedFeatures,
    };

    if (!isEdit) {
      payload.licenceSchema = licenceSchema;
    }

    try {
      if (isEdit) {
        await UlpsPortalApiClient.updateFeatureGroup(featureGroupId, payload);
      } else {
        await UlpsPortalApiClient.createFeatureGroup({
          ...payload,
          licenceSchema,
        });
      }
      window.location.hash = "/feature-groups?success=true";
    } catch (e) {
      console.error(e);
      setError(`Failed to ${isEdit ? "update" : "create"} feature group.`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="d-flex justify-content-center align-items-start">
      <div style={{ maxWidth: "900px", width: "100%" }}>
        <h2>{isEdit ? "Edit Feature Group" : "Create Feature Group"}</h2>
        <br />

        {error && <Alert variant="danger">{error}</Alert>}

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Feature Group Name</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            type="text"
            value={name}
            maxLength={NAME_MAX}
            minLength={NAME_MIN}
            placeholder="Enter feature group name"
            onChange={(e) => setName(e.target.value)}
          />
        </InputGroup>

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Description</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={DESC_MAX}
            value={description}
            placeholder="Short description"
            onChange={(e) => setDescription(e.target.value)}
          />
        </InputGroup>

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Product</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            as="select"
            value={licenceSchema}
            disabled={isEdit}
            onChange={(e) => setLicenceSchema(e.target.value)}
          >
            <option value="">Select a product...</option>
            {licenceSchemas.map((schema) => (
              <option key={schema.name} value={schema.name}>
                {schema.name}
              </option>
            ))}
          </Form.Control>
        </InputGroup>

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Features</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            as="select"
            value=""
            onChange={(e) => {
              const featureId = Number(e.target.value);
              if (!featureId) return;
              const selected = allFeatures.find((feature) => feature.id === featureId);
              if (!selected) return;
              setSelectedFeatures((prev) => [...prev, selected]);
            }}
          >
            <option value="">Select a feature...</option>
            {selectableFeatures.map((feature) => (
              <option key={feature.id} value={feature.id}>
                {feature.description || feature.name}
              </option>
            ))}
          </Form.Control>
        </InputGroup>

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Current Features</InputGroup.Text>
          </InputGroup.Prepend>
          <div className="form-control" style={{ minHeight: "50px", height: "auto" }}>
            {selectedFeatures.length > 0 ? (
              selectedFeatures.map((feature) => (
                <span
                  key={feature.id}
                  style={{
                    padding: "4px 10px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    marginRight: "6px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    backgroundColor: "#f8f9fa",
                  }}
                  onClick={() => setSelectedFeatures((prev) => prev.filter((item) => item.id !== feature.id))}
                >
                  {feature.description || feature.name}
                  <span style={{ fontWeight: "bold", fontSize: "18px", color: "#dc3545" }}>×</span>
                </span>
              ))
            ) : (
              <span style={{ color: "#6c757d" }}>No features selected</span>
            )}
          </div>
        </InputGroup>

        <Button variant="success" onClick={submit} disabled={saving}>
          {saving ? "Saving..." : isEdit ? "Update Feature Group" : "Create Feature Group"}
        </Button>

        <Button
          variant="secondary"
          className="ml-2"
          onClick={() => (window.location.href = "#/feature-groups")}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
